import React, { memo, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { WidgetView, WidgetViewProps } from 'live/components/widget/widget-view'
import { getSpan, getWidgetSpan } from './utils/widgets/getWidgetSpan'
import { CropItem, PdfRender } from '../api/utils/types'

const WAIT_SECONDS = 3

export const Chart = memo(function ChartsRender({
    widget,
    width,
    span,
    spanDepth,
    numberOfVisibleAxes,
    position,
    widgetID,
    onLoad,
    filters,
    paddingBottom,
    chartHeader,
    pdfRender,
    cropItem
}: {
    widget: Widget
    filters: any
    width: string
    span: string
    spanDepth: string
    onLoad: () => void
    widgetID: number
    position: number
    paddingBottom: number
    chartHeader: number
    numberOfVisibleAxes: number
    pdfRender: PdfRender
    cropItem: CropItem
}) {
    const containerRef = useRef(null)

    const queriesByChartLayer = {
        loadQueries: false
    }

    const { span: spanFinal, isDepth } = getSpan({
        widget,
        span,
        spanDepth,
        cropItem
    })
    const ticks = isDepth ? cropItem.ticksDepth : cropItem.ticksTime
    useEffect(() => {
        var lastChange = new Date()

        var observer = new MutationObserver(function (mutations) {
            lastChange = new Date()
        })

        const interval = setInterval(() => {
            if (
                queriesByChartLayer.loadQueries &&
                (new Date().getTime() - lastChange.getTime()) / 1000 > WAIT_SECONDS + ticks / 10
            ) {
                clearInterval(interval)
                observer.disconnect()
                onLoad()
            }
        }, 500)

        observer.observe(containerRef?.current, {
            attributes: false,
            childList: true,
            characterData: false,
            subtree: true
        })

        // Cleanup function to clear the timeout if the component unmounts
        return () => {
            clearInterval(interval)
            observer.disconnect()
        }
    }, [])

    const height = `calc(${ticks}in + ${chartHeader}px + ${paddingBottom}px)`

    return (
        <ChartContainer
            style={{
                position: 'relative',
                height: height,
                width
            }}
            key={`chart-view-${widgetID}-${position}`}
            ref={containerRef}
        >
            <WidgetView
                filters={filters}
                widgetTitle={widget?.title || widget?.name || ''}
                span={spanFinal}
                widget={{
                    ...widget,
                    jsonConfig: {
                        ...widget.jsonConfig,
                        span: spanFinal,
                        numberOfVisibleAxes: numberOfVisibleAxes,
                        hideXAxis: true,
                        hideYAxis: true
                    }
                }}
                eventFlowListener={({ type, ...event }: { type: string }, layer) => {
                    switch (String(type)) {
                        case 'progress':
                            break
                        case 'warning':
                            break
                        case 'span':
                            break
                        case 'performance':
                            break

                        case 'start':
                            queriesByChartLayer[`${position}`] = {
                                ...(queriesByChartLayer[`${position}`] || {}),
                                [`${widgetID}-${layer}`]: false
                            }

                            break
                        case 'endHistory':
                            queriesByChartLayer[`${position}`] = {
                                ...(queriesByChartLayer[`${position}`] || {}),
                                [`${widgetID}-${layer}`]: true
                            }

                            break
                        case 'stop':
                            queriesByChartLayer[`${position}`] = {
                                ...(queriesByChartLayer[`${position}`] || {}),
                                [`${widgetID}-${layer}`]: true
                            }
                            break
                        default:
                            break
                    }
                    if (
                        !queriesByChartLayer.loadQueries &&
                        Object.values(queriesByChartLayer).some(
                            queries => !Object.values(queries).some(value => !value)
                        )
                    ) {
                        queriesByChartLayer.loadQueries = true
                    }
                }}
            />
        </ChartContainer>
    )
})

export const ChartContainer = styled.div`
    border-right: solid 1px var(--gray-600);
`
